import { Component, forwardRef, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { EnumHelper, MbsSize, ModalService, ModalSettings } from 'mbs-ui-kit';
import { Period, PeriodDisplay } from 'mbs-ui-shared@models/Period.enum';
import { combineLatest } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { GFSPolicySettings, RetentionPolicyStepValue } from 'wizards/models/retention-policy-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { NBFGFSModalComponent } from 'wizards/steps/retention-policy-step/NBF/GFS-modal/GFS-modal.component';
import { StepBase } from 'wizards/steps/StepBase.class';
import {
  getGFSPolicyFormGroup,
  isKeepBackupForCorrect,
  monthlyMoreYValidator,
  moreTharRetention,
  weeklyMoreMYValidator
} from './retention-policy-step.utils';

const NBFRetentionPolicyStepValueAccessor: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => NBFRetentionPolicyStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-retention-policy-step',
  templateUrl: './retention-policy-step.component.html',
  providers: [NBFRetentionPolicyStepValueAccessor]
})
export class NBFRetentionPolicyStepComponent extends StepBase<RetentionPolicyStepValue> implements OnInit {
  public periodEnumSelectArray = EnumHelper.EnumToSelectIndexesArray(Period, PeriodDisplay);
  public keepBackupFullAlert: boolean;
  public keepBackupFFIAlert: string;
  public needMinPeriodAlert = false;
  public intelligentRetention = false;
  public needRetentionForGFSAlert = false;
  public GFSInfo: Array<string> = null;
  public elementSelectors = {
    id: {
      notScheduleAlert: 'NBF-Retention-not-full-alert',
      simpleInfo: 'NBF-Retention-simple-info',
      simpleBlock: 'NBF-Retention-simple-block',
      simpleBlockCount: 'NBF-Retention-simple-block-count',
      simpleBlockPeriod: 'NBF-Retention-simple-block-period',
      simpleBlockSwitcher: 'NBF-Retention-simple-block-switcher',
      simpleBlockMinPeriodAlert: 'NBF-Retention-simple-block-min-period-alert',
      simpleBlockMinPeriodNotIRAlert: 'NBF-Retention-simple-block-min-period-not-ir-alert',
      simpleBlockRecommendTurnOn: 'NBF-Retention-simple-block-recommend-turn-on',
      advancedInfo: 'NBF-Retention-advanced-info',
      advancedInfoIncrementalSchedule: 'NBF-Retention-advanced-info-incremental-schedule',
      advancedInfoNoIncrementalSchedule: 'NBF-Retention-advanced-info-no-incremental-schedule',
      advancedBlock: 'NBF-Retention-advanced-block',
      advancedBlockMinPeriodAlert: 'NBF-Retention-advanced-block-min-period-alert',
      advancedInfoAlert: 'NBF-Retention-advanced-info-alert',
      needRetentionForGFSAlert: 'NBF-Retention-need-retention-for-GFS-alert',
      switcherGFS: 'NBF-Retention-switcher-GFS',
      buttonGFS: 'NBF-Retention-button-GFS'
    }
  };
  public schedule = {
    isSimple: false,
    isAdvanced: false,
    isManual: false,
    incrementalDataEnabled: true,
    scheduleInfo: {
      simple: '',
      incremental: '',
      full: ''
    },
    incrementalSchedule: null,
    fullSchedule: null
  };
  public storage = {
    name: '',
    minPeriod: null,
    canShowImmutabilityByStorage: false
  };
  private deleteVersionsOlderThanCount: AbstractControl;
  private deleteVersionsOlderThanPeriod: AbstractControl;
  private templates = {
    enabled: this.i18nPipe.transform('shared:common:enabled'),
    disabled: this.i18nPipe.transform('shared:common:disabled'),
    ObjectLock: this.i18nPipe.transform('wizards:retention.objectLock'),
    Keep: this.i18nPipe.transform('wizards:retention.advancedSchedule.keep', { format: 'title' }),
    for: this.i18nPipe.transform('wizards:retention.advancedSchedule.for'),
    week: this.i18nPipe.transform('wizards:common.week'),
    weeks: this.i18nPipe.transform('wizards:common.weeks'),
    Weekly: this.i18nPipe.transform('wizards:common.weekly', { format: 'title' }),
    month: this.i18nPipe.transform('wizards:common.month'),
    months: this.i18nPipe.transform('wizards:common.months'),
    Monthly: this.i18nPipe.transform('wizards:common.monthly', { format: 'title' }),
    year: this.i18nPipe.transform('wizards:common.year'),
    years: this.i18nPipe.transform('wizards:common.years'),
    Yearly: this.i18nPipe.transform('wizards:common.yearly', { format: 'title' })
  };

  constructor(
    public ability: AbilityService,
    public mainService: RemoteManagementWizardsService,
    private modalService: ModalService,
    private i18nPipe: I18NextPipe,
    private stepService: WizardStepsService
  ) {
    super(mainService);
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    this.stepForm = new FormGroup({
      RetentionUseDefaultSettings: new FormControl(false),
      deleteVersionsOlderThanCount: new FormControl(1, [Validators.min(1)]),
      deleteVersionsOlderThanPeriod: new FormControl(),
      GFSPolicySettings: getGFSPolicyFormGroup(this.weeklyMoreMonthlyValidator.bind(this), this.monthlyMoreMonthlyValidator.bind(this))
    });

    this.initFormEvents();
  }

  private initEvents(): void {
    this.stepService
      .pipeProperty('schedule')
      .pipe(untilDestroyed(this), debounceTime(300))
      .subscribe((schedule) => {
        const oldData = JSON.stringify(this.schedule);
        const newDataMap = {
          isSimple: schedule.isSimpleMode,
          isAdvanced: schedule.isAdvancedMode,
          isManual: schedule.isManualMode,
          incrementalDataEnabled: schedule.incrementalDataEnabled,
          scheduleInfo: schedule.scheduleInfo,
          incrementalSchedule: schedule.incrementalSchedule,
          fullSchedule: schedule.fullSchedule
        };
        const newData = JSON.stringify(newDataMap);

        if (oldData != newData) {
          this.schedule = newDataMap;

          if (this.schedule.isSimple) {
            this.stepForm.get('RetentionUseDefaultSettings').setValue(false);
            this.stepForm.get('deleteVersionsOlderThanCount').enable();
            this.stepForm.get('deleteVersionsOlderThanPeriod').enable();

            this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);
          }

          this.updateGFS();
          this.validateRetention();
          this.stepForm.updateValueAndValidity();
        }
      });

    this.stepService
      .pipeProperty('storage')
      .pipe(untilDestroyed(this), debounceTime(300))
      .subscribe((storage) => {
        const oldData = JSON.stringify(this.storage);
        const newDataMap = {
          name: storage.name,
          minPeriod: storage.minPeriod,
          canShowImmutabilityByStorage: storage.canShowImmutabilityByStorage
        };
        const newData = JSON.stringify(newDataMap);

        if (oldData != newData) {
          const needUpdateMinPeriodAlert = this.storage.name !== storage.name || this.storage.minPeriod !== storage.minPeriod;
          this.storage = newDataMap;

          if (!this.storage.canShowImmutabilityByStorage) {
            this.stepForm.get('GFSPolicySettings').get('enabledImmutability').setValue(false);
            this.updateGFSInfo();
          }

          this.stepForm.updateValueAndValidity();
          this.validateRetention();

          if (needUpdateMinPeriodAlert) {
            this.updateMinPeriodAlert();
          }
        }
      });
  }

  protected initFormEvents(): void {
    this.deleteVersionsOlderThanCount = this.stepForm.get('deleteVersionsOlderThanCount');
    this.deleteVersionsOlderThanPeriod = this.stepForm.get('deleteVersionsOlderThanPeriod');

    combineLatest([this.deleteVersionsOlderThanCount.valueChanges, this.deleteVersionsOlderThanPeriod.valueChanges]).subscribe(() => {
      this.validateRetention();
    });

    super.initFormEvents();
  }

  onStepFormChange(value: RetentionPolicyStepValue): void {
    const goodKeepBackup = !this.keepBackupFFIAlert;

    this.needRetentionForGFSAlert = value?.RetentionUseDefaultSettings && value?.GFSPolicySettings.enabled;

    this.value = { ...value, valid: this.stepForm.valid && !this.needRetentionForGFSAlert && goodKeepBackup };

    this.stepService.updateProperty('retention', 'isGFSMode', value.GFSPolicySettings.enabled);
  }

  updateForm(value: RetentionPolicyStepValue): void {
    this.intelligentRetention = value?.intelligentRetention;
    this.stepForm.reset(value);

    this.isValid.emit(this.value.valid);
    this.updateGFSInfo();
    this.toggleFormControls(
      ['deleteVersionsOlderThanCount', 'deleteVersionsOlderThanPeriod'],
      !this.stepForm.get('RetentionUseDefaultSettings').value
    );
  }

  retentionUseDefaultSettingsChangeHandler(): void {
    queueMicrotask(() => {
      const value = this.stepForm.get('RetentionUseDefaultSettings').value;

      this.toggleFormControls(['deleteVersionsOlderThanCount', 'deleteVersionsOlderThanPeriod'], value);
      this.stepForm.get('RetentionUseDefaultSettings').setValue(!value);

      if (!value) {
        this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);

        this.updateGFSInfo();
      }
    });
  }

  retentionChangeHandler(): void {
    this.stepForm.get('deleteVersionsOlderThanCount').updateValueAndValidity();
  }

  GFSEnabledChangeHandler(): void {
    const newValue = !this.stepForm.get('GFSPolicySettings').get('enabled').value;

    this.stepForm.get('GFSPolicySettings').get('enabled').setValue(newValue);

    if (newValue) {
      queueMicrotask(() => this.clickDetailsGFS(true));
    }

    this.updateGFSInfo();
    this.GFSValidator();
  }

  clickDetailsGFS(needReset?: boolean): void {
    const final = () => {
      if (needReset) {
        this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);

        this.updateGFSInfo();
      }
    };
    const settings: ModalSettings = {
      ...new ModalSettings(),
      size: MbsSize.sm,
      data: {
        schedule: this.schedule,
        canShowImmutabilityByStorage: this.storage.canShowImmutabilityByStorage,
        GFS: this.stepForm.get('GFSPolicySettings').value,
        retention: {
          deleteVersionsOlderThanCount: this.stepForm.get('deleteVersionsOlderThanCount').value,
          deleteVersionsOlderThanPeriod: this.stepForm.get('deleteVersionsOlderThanPeriod').value
        }
      }
    };

    this.modalService
      .openCustom(NBFGFSModalComponent, settings)
      .then((result: { data: GFSPolicySettings }) => {
        if (!result) return void final();

        const weekly = result.data.weekly.enabled;
        const monthly = result.data.monthly.enabled;
        const yearly = result.data.yearly.enabled;

        this.stepForm.get('GFSPolicySettings').reset({ ...result.data, enabled: weekly || monthly || yearly });
        this.updateGFSInfo();
        this.GFSValidator();
      })
      .catch(final);
  }

  /**
   * Just composition point
   * @private
   */
  private validateRetention(): void {
    this.keepBackupForIncrementalValidator();
    this.keepBackupForFullValidator();
    this.GFSValidator();

    if (this.storage.minPeriod) {
      this.updateMinPeriodAlert();
    }
  }

  /**
   * Depends of Schedule Step data
   * @return {null}
   */
  private keepBackupForIncrementalValidator(): void {
    if (!this.schedule.isSimple || !this.schedule.incrementalSchedule) {
      this.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'keepBackupPeriodInvalid');

      return void delete this.keepBackupFFIAlert;
    }

    const selected: { count: number; period: Period } = {
      count: this.deleteVersionsOlderThanCount.value,
      period: this.deleteVersionsOlderThanPeriod.value
    };

    const incrementalKeepBackupResult = isKeepBackupForCorrect(this.schedule.incrementalSchedule, selected);

    if (!incrementalKeepBackupResult.error) {
      this.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'keepBackupPeriodInvalid');

      return void delete this.keepBackupFFIAlert;
    }

    this.keepBackupFFIAlert = this.i18nPipe.transform('wizards:retention.keepBackupFFIErrorMessage', {
      count: incrementalKeepBackupResult.count,
      period: this.periodEnumSelectArray[incrementalKeepBackupResult.period].label.toLocaleString().toLowerCase().split('(')[0]
    });

    this.deleteVersionsOlderThanCount.setErrors({ keepBackupPeriodInvalid: true });
    this.deleteVersionsOlderThanPeriod.setErrors({ keepBackupPeriodInvalid: true });

    this.value = { ...this.value, valid: false };
  }

  /**
   * Depends of Schedule Step data
   * @return {null}
   */
  private keepBackupForFullValidator(): void {
    if (this.schedule.isManual || this.schedule.isSimple || !this.schedule.fullSchedule) {
      return void delete this.keepBackupFullAlert;
    }

    const selected: { count: number; period: Period } = {
      count: this.deleteVersionsOlderThanCount.value,
      period: this.deleteVersionsOlderThanPeriod.value
    };

    const fullKeepBackupResult = isKeepBackupForCorrect(this.schedule.fullSchedule, selected);

    if (!fullKeepBackupResult.error) {
      return void delete this.keepBackupFullAlert;
    }

    this.keepBackupFullAlert = true;
  }

  private GFSValidator(): void {
    const enabledGFS = this.stepForm.get('GFSPolicySettings').get('enabled').value;
    const retention = {
      count: this.stepForm.get('deleteVersionsOlderThanCount').value,
      period: this.stepForm.get('deleteVersionsOlderThanPeriod').value
    };
    const hasError = ['weekly', 'monthly', 'yearly'].reduce((result, key) => {
      const controlValue = this.stepForm.get('GFSPolicySettings').get(key).get('storePeriod').value;
      const enabled = this.stepForm.get('GFSPolicySettings').get(key).get('enabled').value;

      if (!enabled || !enabledGFS) {
        return result;
      }

      let period: Period;

      switch (key) {
        case 'weekly':
          period = Period.week;
          break;
        case 'monthly':
          period = Period.month;
          break;
        case 'yearly':
          period = Period.year;
          break;
        default:
          break;
      }

      const isOk = moreTharRetention({ count: controlValue, period }, retention);

      if (!isOk) {
        return true;
      }

      return result;
    }, false);

    if (hasError) {
      this.deleteVersionsOlderThanCount.setErrors({ GFSPeriodInvalid: true });
      this.deleteVersionsOlderThanPeriod.setErrors({ GFSPeriodInvalid: true });

      return void 0;
    }

    this.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'GFSPeriodInvalid');
  }

  private weeklyMoreMonthlyValidator(control: AbstractControl): ValidationErrors | null {
    return weeklyMoreMYValidator(this.stepForm?.get('GFSPolicySettings'), control);
  }

  private monthlyMoreMonthlyValidator(control: AbstractControl): ValidationErrors | null {
    return monthlyMoreYValidator(this.stepForm?.get('GFSPolicySettings'), control);
  }

  private updateMinPeriodAlert(): void {
    this.needMinPeriodAlert = false;

    if (!this.storage.minPeriod) return;

    if (this.stepForm.get('RetentionUseDefaultSettings').value) {
      return;
    }

    const count = this.stepForm.get('deleteVersionsOlderThanCount').value;
    const period = this.stepForm.get('deleteVersionsOlderThanPeriod').value;
    let reason: boolean;

    switch (period) {
      case Period.year: {
        reason = false;
        break;
      }
      case Period.day: {
        reason = count < this.storage.minPeriod;
        break;
      }
      case Period.week: {
        reason = count * 7 < this.storage.minPeriod;
        break;
      }
      default: {
        reason = count * 30 < this.storage.minPeriod;
      }
    }

    this.needMinPeriodAlert = reason;
  }

  private updateGFS(): void {
    const scheduleMonthly = this.schedule?.fullSchedule?.recurringPeriod === 'Monthly';
    const scheduleCount = this.schedule?.fullSchedule?.repeatEveryCount || 0;

    if (scheduleMonthly ? scheduleCount < 12 : scheduleCount >= 30) this.offGFSFields(['weekly']);

    if (scheduleMonthly && scheduleCount >= 12) this.offGFSFields(['weekly', 'monthly']);

    this.updateGFSInfo();
  }

  private offGFSFields(names: string[]): void {
    const gfsControl = this.stepForm.get('GFSPolicySettings');

    names.forEach((name: string) => {
      gfsControl.get(name).get('enabled').setValue(false);
    });
  }

  private updateGFSInfo(): void {
    const GFSPolicySettings = this.stepForm.get('GFSPolicySettings').value;

    if (!GFSPolicySettings.enabled) {
      return void delete this.GFSInfo;
    }

    const t = this.templates;
    const weekly = GFSPolicySettings.weekly?.enabled ? GFSPolicySettings.weekly.storePeriod : '';
    const monthly = GFSPolicySettings.monthly?.enabled ? GFSPolicySettings.monthly.storePeriod : '';
    const yearly = GFSPolicySettings.yearly?.enabled ? GFSPolicySettings.yearly.storePeriod : '';
    const keep = weekly || monthly || yearly;
    const objectLock = GFSPolicySettings.enabledImmutability;

    // Weekly for 1 week / Weekly for 2 weeks
    const weeklyString = weekly ? `${t.Weekly} ${t.for} ${weekly} ${weekly > 1 ? t.weeks : t.week}` : '';
    // Monthly for 1 month / Monthly for 2 months
    const monthlyString = monthly ? `${t.Monthly} ${t.for} ${monthly} ${monthly > 1 ? t.months : t.month}` : '';
    // Yearly for 1 year / Yearly for 2 years
    const yearlyString = yearly ? `${t.Yearly} ${t.for} ${yearly} ${yearly > 1 ? t.years : t.year}` : '';

    this.GFSInfo = [];

    if (keep) {
      this.GFSInfo.push(`${t.Keep} ${[weeklyString, monthlyString, yearlyString].filter((s) => !!s).join(', ')}.`);
    }

    this.GFSInfo.push(`${t.ObjectLock}: ${objectLock ? t.enabled : t.disabled}`);
  }
}
